import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs, doc, getDoc, setDoc, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db, auth } from "./firebase";
import StudentLayout from "./StudentLayout";

const HomeworkPage = () => {
  const [homeworkAssignments, setHomeworkAssignments] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [shrinkingCards, setShrinkingCards] = useState({});
  const navigate = useNavigate();
  const [showSubmitted, setShowSubmitted] = useState(false);

  useEffect(() => {
    fetchHomeworkAssignments();
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes shrinkCard {
        0% {
          opacity: 1;
          height: auto;
          transform: scale(1);
          margin-bottom: 1rem;
          padding: 1rem 1.5rem;
        }
        100% {
          opacity: 0;
          height: 0;
          transform: scale(0.8);
          margin: 0;
          padding: 0;
          overflow: hidden;
        }
      }

      .shrinking-card {
        animation: shrinkCard 0.5s forwards ease-in-out;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchHomeworkAssignments = async () => {
    const user = auth.currentUser;
    if (!user) {
      console.error("No authenticated user found.");
      return;
    }

    const homeworkRef = collection(db, "homework");
    const q = query(homeworkRef, where("studentUID", "==", user.uid));

    try {
      const [homeworkSnap, assignmentSnap] = await Promise.all([
        getDocs(q),
        getDocs(collection(db, "assignments")),
      ]);

      const allAssignments = assignmentSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const homeworkList = homeworkSnap.docs.map((doc) => {
        const data = doc.data();
        const assignmentObjects = (data.assignments || []).map((id) =>
          allAssignments.find((a) => a.id === id)
        ).filter(Boolean);

        return {
          id: doc.id,
          ...data,
          assignmentObjects,
        };
      });

      setHomeworkAssignments(homeworkList);
      const allWithContext = homeworkList.flatMap(hw => {
  if (hw.assignmentObjects.length === 0) {
    return [{
      id: `no-assignment-${hw.id}`,
      title: "(Resource only)",
      homeworkTitle: hw.title,
      dueDate: hw.dueDate,
      setDate: hw.createdAt,
      notes: hw.notes,
      googleResources: hw.googleResources,
      hwId: hw.id,
      noAssignment: true
    }];
  }

  return hw.assignmentObjects.map(a => ({
    ...a,
    homeworkTitle: hw.title,
    dueDate: hw.dueDate,
    setDate: hw.createdAt,
    notes: hw.notes,
    googleResources: hw.googleResources,
    hwId: hw.id
  }));
});

    
      fetchStudentProgress(allWithContext);
      
    } catch (error) {
      console.error("Error fetching homework assignments:", error);
    }
  };

  const fetchStudentProgress = async (assignments) => {
    const user = auth.currentUser;
    if (!user) return;

    const map = {};
    for (const assignment of assignments) {
      const progressRef = doc(db, "assignmentProgress", `${assignment.id}_${user.uid}`);
      const snap = await getDoc(progressRef);
      if (snap.exists()) {
        const data = snap.data();
        map[assignment.id] = {
          completed: data.completed || false,
          handedIn: data.handedIn || false,
          started: data.started || false,
        };
      } else {
        map[assignment.id] = {
          completed: false,
          handedIn: false,
          started: false,
        };
      }
    }

    setProgressMap(map);
  };

  const handleHandIn = async (assignmentId) => {
    const user = auth.currentUser;
    if (!user) return;

    const progressRef = doc(db, "assignmentProgress", `${assignmentId}_${user.uid}`);
    const current = (await getDoc(progressRef)).data() || {};

    await setDoc(progressRef, {
      ...current,
      handedIn: true,
    }, { merge: true });

    setShrinkingCards(prev => ({ ...prev, [assignmentId]: true }));

    setTimeout(() => {
      setProgressMap(prev => ({
        ...prev,
        [assignmentId]: {
          ...prev[assignmentId],
          handedIn: true,
        },
      }));
    }, 600);
  };

  const getStatusText = (assignmentId) => {
    const progress = progressMap[assignmentId];
    if (!progress) return "Not Started";
    if (progress.handedIn) return "✅ Handed In";
    if (progress.completed) return "✅ Completed";
    return "⏳ In Progress";
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const now = new Date();
    const due = new Date(dueDate);
    return now > due;
  };
  const allAssignments = homeworkAssignments.flatMap(hw => {
  if (!hw.assignmentObjects || hw.assignmentObjects.length === 0) {
    return [{
      id: `no-assignment-${hw.id}`,
      title: "(Resource only)",
      homeworkTitle: hw.title,
      dueDate: hw.dueDate,
      setDate: hw.createdAt,
      notes: hw.notes,
      googleResources: hw.googleResources,
      hwId: hw.id,
      noAssignment: true
    }];
  }

  return hw.assignmentObjects.map(a => ({
    ...a,
    homeworkTitle: hw.title,
    dueDate: hw.dueDate,
    setDate: hw.createdAt,
    notes: hw.notes,
    googleResources: hw.googleResources,
    hwId: hw.id
  }));
});

  
  const now = new Date();

const { homeworkDue, homeworkOverdue, homeworkHandedIn } = useMemo(() => {
  const due = [];
  const overdue = [];
  const handedIn = [];

  for (const a of allAssignments) {
    const progress = progressMap[a.id];

    if (progress?.handedIn) {
      handedIn.push(a);
    } else if (a.dueDate) {
      const dueDate = new Date(a.dueDate);
      if (dueDate < now) {
        overdue.push(a);
      } else {
        due.push(a);
      }
    } else {
      due.push(a); // No due date = considered due
    }
  }

  return { homeworkDue: due, homeworkOverdue: overdue, homeworkHandedIn: handedIn };
}, [allAssignments, progressMap]);

  const renderAssignmentCard = (a) => (
    <div
  key={a.id}
  style={{
    ...styles.card,
    ...(progressMap[a.id]?.handedIn ? styles.cardSubmitted : {})
  }}
>

      <div style={styles.cardLeft}>
        <div style={styles.title}>
  {a.title === "(Resource only)" ? a.homeworkTitle : a.title}
</div>

        <div style={styles.dateInfo}>
          <div>📅 Set: {new Date(a.setDate).toLocaleDateString()}</div>
          {a.dueDate && (
            <div style={{
              ...styles.dueDate,
              color: isOverdue(a.dueDate) ? "#d63031" : "#2d3436"
            }}>
              ⏳ Due: {new Date(a.dueDate).toLocaleDateString()}
            </div>
          )}
        </div>
        {a.notes && <p style={{ fontSize: "0.95rem" }}>{a.notes}</p>}
        {a.googleResources?.length > 0 && (
          <ul>
            {a.googleResources.map((res, i) => (
              <li key={i}><a href={res.url} target="_blank" rel="noreferrer">{res.name}</a></li>
            ))}
          </ul>
        )}
  
        <span style={{
          ...styles.pill,
          ...(progressMap[a.id]?.handedIn
            ? styles.handedIn
            : progressMap[a.id]?.completed
            ? styles.completed
            : progressMap[a.id]?.started
            ? styles.inProgress
            : styles.notStarted),
        }}>
          {getStatusText(a.id)}
        </span>
  
      <div style={styles.btnRow}>
  {!a.noAssignment && (
    <button style={styles.viewBtn} onClick={() => navigate(`/student-assignment/${a.id}`)}>
      {progressMap[a.id]?.handedIn
        ? "🔁 Review Homework"
        : progressMap[a.id]?.completed
        ? "▶️ Continue Homework"
        : progressMap[a.id]?.started
        ? "▶️ Continue Homework"
        : "📝 Start Homework"}
    </button>
  )}

  {!progressMap[a.id]?.handedIn && (
    <button style={styles.submitBtn} onClick={() => handleHandIn(a.id)}>
      📤 Hand In
    </button>
  )}
</div>


     
      </div>
    </div>
  );
  
  return (
    <StudentLayout>
      <div style={styles.container}>
        <h1 style={styles.heading}>📚 Homework</h1>

        {allAssignments.length === 0 ? (
  <p>No homework assigned yet.</p>
) : (
  <>
  {homeworkOverdue.length > 0 && (
    <>
      <h2 style={{ marginTop: "2rem", color: "#d63031" }}>⚠️ Overdue Homework</h2>
      {homeworkOverdue.map(a => renderAssignmentCard(a))}
    </>
  )}

  <h2 style={{ marginTop: "2rem" }}>📘 Homework To Do</h2>
{homeworkDue.length === 0 && homeworkOverdue.length === 0 ? (
  <p style={{ fontStyle: "italic", color: "#636e72", marginTop: "0.5rem" }}>
    🎉 Yay! You have completed all your homework.
  </p>
) : (
  homeworkDue.map(a => renderAssignmentCard(a))
)}


{homeworkHandedIn.length > 0 && (
  <div style={{ marginTop: "2rem" }}>
    <h2
      onClick={() => setShowSubmitted(prev => !prev)}
      style={{
        color: "#2d3436",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        userSelect: "none"
      }}
    >
      {showSubmitted ? "▼" : "▶"} ✅ Homework Submitted
    </h2>
    {showSubmitted && homeworkHandedIn.map(a => renderAssignmentCard(a))}
  </div>
)}

</>

)}

      </div>
    </StudentLayout>
  );
};

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Fredoka', cursive",
    backgroundColor: "#fefae0",
    minHeight: "100vh",
  },
  heading: {
    fontSize: "2rem",
    marginBottom: "1.5rem",
  },
  card: {
    backgroundColor: "#fff",
    padding: "1rem 1.5rem",
    borderRadius: "10px",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
    marginBottom: "1rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  cardHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
  },
  cardLeft: {
    flex: 1,
  },
  title: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginBottom: "0.25rem",
  },
  dateInfo: {
    fontSize: "0.9rem",
    margin: "0.3rem 0 0.6rem",
  },
  dueDate: {
    fontSize: "1rem",
    fontWeight: "bold",
    marginTop: "0.2rem",
  },
  pill: {
    fontSize: "0.9rem",
    fontWeight: "bold",
    padding: "0.3rem 0.8rem",
    borderRadius: "999px",
    display: "inline-block",
    backgroundColor: "#dfe6e9",
    color: "#2d3436",
    marginTop: "0.5rem",
  },
  completed: { backgroundColor: "#55efc4", color: "#00695c" },
  handedIn: { backgroundColor: "#a29bfe", color: "#2d3436" },
  inProgress: { backgroundColor: "#ffeaa7", color: "#2d3436" },
  notStarted: { backgroundColor: "#dfe6e9", color: "#2d3436" },
  btnRow: {
    display: "flex",
    gap: "0.5rem",
    alignItems: "center",
  },
  viewBtn: {
    backgroundColor: "#0984e3",
    color: "#fff",
    padding: "0.4rem 0.8rem",
    fontSize: "0.9rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  submitBtn: {
    backgroundColor: "#00b894",
    color: "#fff",
    padding: "0.4rem 0.8rem",
    fontSize: "0.9rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  cardSubmitted: {
  backgroundColor: "#f1f2f6", // Light silver tone
  border: "1px solid #dcdde1"
},

};

export default HomeworkPage;
